/* ==========================================================================
   LOUREL — Product section controller
   Self-contained, dependency-free. One controller instance per section so it
   works in the theme editor (multiple/duplicated sections) and on live PDPs.

   Responsibilities:
     - Accordions (collapsible rows)
     - Quantity stepper
     - Dynamic variant picker bound to the product's real options
     - Price / availability updates on variant change + URL sync
     - AJAX add-to-cart that integrates with the theme's cart drawer when
       present, and falls back to the cart page otherwise.
   ========================================================================== */
(function () {
  "use strict";

  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || "${{amount}}";

    function defaultTo(num, precision, thousands, decimal) {
      thousands = thousands === undefined ? "," : thousands;
      decimal = decimal === undefined ? "." : decimal;
      if (isNaN(num) || num == null) return 0;
      num = (num / 100).toFixed(precision);
      var parts = num.split(".");
      var dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands
      );
      var centsPart = parts[1] ? decimal + parts[1] : "";
      return dollars + centsPart;
    }

    var match = fmt.match(placeholderRegex);
    var token = match ? match[1] : "amount";
    switch (token) {
      case "amount":
        value = defaultTo(cents, 2);
        break;
      case "amount_no_decimals":
        value = defaultTo(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = defaultTo(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = defaultTo(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = defaultTo(cents, 2, "'", ".");
        break;
      case "amount_with_space_separator":
        value = defaultTo(cents, 2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = defaultTo(cents, 0, " ", "");
        break;
      default:
        value = defaultTo(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  function LourelProduct(root) {
    this.root = root;
    this.sectionId = root.getAttribute("data-section-id");
    this.config = this.readJSON("[data-lp-config-json]") || {};
    this.variants = this.readJSON("[data-lp-variant-json]") || [];
    this.moneyFormat = this.config.moneyFormat || "${{amount}}";
    this.form = root.querySelector('[data-type="add-to-cart-form"]');
    this.idInput = root.querySelector("[data-lp-variant-id]");
    this.submitBtn = root.querySelector("[data-lp-submit]");
    this.submitText = root.querySelector("[data-lp-submit-text]");
    this.spinner = root.querySelector(".lp-add-btn__spinner");
    this.priceWrap = root.querySelector("[data-lp-price-wrap]");
    this.errorBox = root.querySelector("[data-lp-error]");
    this.errorText = root.querySelector("[data-lp-error-text]");

    this.variantMedia = this.readJSON("[data-lp-variant-media]") || {};
    this.gallery = root.querySelector(".lp-gallery");

    this.initAccordions();
    this.initQuantity();
    this.initVariants();
    this.initAddToCart();
  }

  LourelProduct.prototype.readJSON = function (selector) {
    var el = this.root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  /* ----------------------------- Accordions ----------------------------- */
  LourelProduct.prototype.initAccordions = function () {
    var rows = this.root.querySelectorAll("[data-lp-accordion]");
    rows.forEach(function (row) {
      var head = row.querySelector(".lp-accordion__head");
      if (!head) return;
      head.addEventListener("click", function () {
        var open = row.classList.toggle("is-open");
        head.setAttribute("aria-expanded", open ? "true" : "false");
      });
    });
  };

  /* ------------------------------ Quantity ------------------------------ */
  LourelProduct.prototype.initQuantity = function () {
    var input = this.root.querySelector("[data-lp-qty-input]");
    if (!input) return;
    this.root.querySelectorAll("[data-lp-qty]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var dir = btn.getAttribute("data-lp-qty");
        var val = parseInt(input.value, 10) || 1;
        val = dir === "plus" ? val + 1 : Math.max(1, val - 1);
        input.value = val;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  };

  /* ------------------------------ Variants ------------------------------ */
  LourelProduct.prototype.initVariants = function () {
    var wrap = this.root.querySelector("[data-lp-variants]");
    if (!wrap) {
      // Single-variant product: nothing to pick.
      this.currentVariant = this.variants[0] || null;
      return;
    }
    var self = this;
    this.optionInputs = Array.prototype.slice.call(
      wrap.querySelectorAll(".lp-swatch__input")
    );
    this.optionInputs.forEach(function (input) {
      input.addEventListener("change", function () {
        self.onVariantChange();
      });
    });
    this.onVariantChange(true);

    // Self-healing: if anything reorders/re-renders the gallery (theme morph,
    // a late async update, rapid toggling), re-assert the selected colour's photo
    // as the first image so it can never desync from the chosen variant.
    if (this.gallery && window.MutationObserver) {
      this._galObserver = new MutationObserver(function () {
        self.syncGallery(false);
      });
      this._galObserver.observe(this.gallery, { childList: true });
    }
  };

  LourelProduct.prototype.getSelectedOptions = function () {
    // Collect the checked value for each option group, ordered by position.
    var groups = this.root.querySelectorAll("[data-lp-variants] .lp-option");
    var selected = [];
    groups.forEach(function (group) {
      var checked = group.querySelector(".lp-swatch__input:checked");
      selected.push(checked ? checked.value : null);
    });
    return selected;
  };

  LourelProduct.prototype.findVariant = function (selected) {
    return (
      this.variants.find(function (v) {
        return selected.every(function (val, i) {
          return v.options[i] === val;
        });
      }) || null
    );
  };

  LourelProduct.prototype.onVariantChange = function (isInit) {
    var selected = this.getSelectedOptions();
    var variant = this.findVariant(selected);
    this.currentVariant = variant;

    this.updateAvailabilityHints(selected);
    this.updateSwatchNames();
    this.syncGallery(isInit);

    if (!variant) {
      this.setUnavailable(this.config.unavailableText || "Unavailable");
      if (this.idInput) this.idInput.value = "";
      return;
    }

    if (this.idInput) {
      this.idInput.value = variant.id;
      this.idInput.removeAttribute("disabled");
    }

    this.updatePrice(variant);
    this.updateButton(variant);
    if (!isInit) this.updateURL(variant);
  };

  LourelProduct.prototype.updatePrice = function (variant) {
    if (!this.priceWrap) return;
    var html = "";
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html =
        '<span class="lp-price__sale">' +
        formatMoney(variant.price, this.moneyFormat) +
        "</span>" +
        '<s class="lp-price__compare">' +
        formatMoney(variant.compare_at_price, this.moneyFormat) +
        "</s>";
    } else {
      html =
        '<span class="lp-price__regular">' +
        formatMoney(variant.price, this.moneyFormat) +
        "</span>";
    }
    this.priceWrap.innerHTML = html;
  };

  LourelProduct.prototype.updateButton = function (variant) {
    if (!this.submitBtn) return;
    if (variant.available) {
      this.submitBtn.removeAttribute("disabled");
      if (this.submitText)
        this.submitText.textContent =
          this.config.addToCartText || "Add to Bag";
    } else {
      this.setUnavailable(this.config.soldOutText || "Sold Out");
    }
  };

  LourelProduct.prototype.setUnavailable = function (text) {
    if (!this.submitBtn) return;
    this.submitBtn.setAttribute("disabled", "disabled");
    if (this.submitText) this.submitText.textContent = text;
  };

  // Mark option values that lead to no/unavailable variants (soft hint).
  LourelProduct.prototype.updateAvailabilityHints = function (selected) {
    var self = this;
    var groups = this.root.querySelectorAll("[data-lp-variants] .lp-option");
    groups.forEach(function (group, gIndex) {
      var labels = group.querySelectorAll(".lp-swatch");
      labels.forEach(function (label) {
        var input = document.getElementById(label.getAttribute("for"));
        if (!input) return;
        var trial = selected.slice();
        trial[gIndex] = input.value;
        var exists = self.variants.some(function (v) {
          var ok = true;
          for (var i = 0; i < trial.length; i++) {
            if (trial[i] != null && i !== gIndex && v.options[i] !== trial[i]) {
              ok = false;
              break;
            }
          }
          return ok && v.options[gIndex] === input.value && v.available;
        });
        label.classList.toggle("lp-swatch--unavailable", !exists);
      });
    });
  };

  // Reflect the chosen swatch value's name in the option label (e.g. "Mint Green").
  LourelProduct.prototype.updateSwatchNames = function () {
    var groups = this.root.querySelectorAll("[data-lp-variants] .lp-option");
    groups.forEach(function (group) {
      var nameEl = group.querySelector("[data-lp-swatch-name]");
      if (!nameEl) return;
      var checked = group.querySelector(".lp-swatch__input:checked");
      if (checked) nameEl.textContent = checked.value;
    });
  };

  // Ensure the CURRENTLY selected variant's assigned photo is the first/main image
  // (and slide 1 of the mobile carousel). Reads live state each call, so it's safe
  // to run from the variant-change handler AND from the gallery MutationObserver —
  // it self-corrects to the chosen colour and can never lock onto a stale image.
  // Idempotent: does nothing if the correct image is already first (no observer loop).
  LourelProduct.prototype.syncGallery = function (isInit) {
    if (!this.gallery) return;
    var variant = this.currentVariant;
    if (!variant) return;
    var mediaId = this.variantMedia[variant.id];
    if (mediaId == null) return;
    var target = this.gallery.querySelector('.lp-media[data-media-id="' + mediaId + '"]');
    if (!target || this.gallery.firstElementChild === target) return;

    this.gallery.insertBefore(target, this.gallery.firstElementChild);

    try { this.gallery.scrollTo({ left: 0, behavior: isInit ? "auto" : "smooth" }); }
    catch (e) { this.gallery.scrollLeft = 0; }
    var fill = this.root.querySelector("[data-lp-bar-fill]");
    if (fill) fill.style.transform = "translateX(0)";
  };

  LourelProduct.prototype.updateURL = function (variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url.toString());
  };

  /* ---------------------------- Add to cart ----------------------------- */
  LourelProduct.prototype.initAddToCart = function () {
    if (!this.form) return;

    // If the host theme already defines a <product-form> custom element, it
    // owns submission + cart-drawer rendering. Avoid double-binding.
    this.themeHandlesForm =
      typeof window.customElements !== "undefined" &&
      !!window.customElements.get("product-form");

    if (this.themeHandlesForm) return;

    var self = this;
    this.form.addEventListener("submit", function (evt) {
      evt.preventDefault();
      self.submit();
    });
  };

  LourelProduct.prototype.cartAddUrl = function () {
    return (window.routes && window.routes.cart_add_url) || "/cart/add";
  };
  LourelProduct.prototype.cartUrl = function () {
    return (window.routes && window.routes.cart_url) || "/cart";
  };

  LourelProduct.prototype.submit = function () {
    var self = this;
    if (this.submitBtn && this.submitBtn.hasAttribute("disabled")) return;

    this.showError("");
    this.setLoading(true);

    var formData = new FormData(this.form);
    var cart = document.querySelector("cart-drawer");
    var hasDrawer =
      cart && typeof cart.getSectionsToRender === "function";

    if (hasDrawer && this.config.afterAddToCart === "drawer") {
      try {
        formData.append(
          "sections",
          cart
            .getSectionsToRender()
            .map(function (s) {
              return s.id;
            })
            .join(",")
        );
        formData.append("sections_url", window.location.pathname);
      } catch (e) {}
    }

    fetch(this.cartAddUrl(), {
      method: "POST",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        self.setLoading(false);
        if (!result.ok || result.data.status) {
          self.showError(
            result.data.description ||
              result.data.message ||
              "Could not add to bag."
          );
          return;
        }
        self.afterAdd(result.data, cart, hasDrawer);
      })
      .catch(function () {
        self.setLoading(false);
        self.showError("Network error. Please try again.");
      });
  };

  LourelProduct.prototype.afterAdd = function (data, cart, hasDrawer) {
    var mode = this.config.afterAddToCart || "drawer";

    // Notify any listeners (theme cart bubbles, etc.)
    document.dispatchEvent(
      new CustomEvent("lourel:cart:added", { detail: { product: data } })
    );

    if (mode === "drawer" && hasDrawer) {
      try {
        cart.renderContents(data);
        if (typeof cart.open === "function") cart.open();
        // Some themes use a header trigger element instead.
        var bubble = document.querySelector(
          "cart-drawer, #cart-notification, .cart-drawer"
        );
        if (bubble && bubble.classList) bubble.classList.remove("is-empty");
        return;
      } catch (e) {
        /* fall through to redirect */
      }
    }

    if (mode === "none") {
      this.flashAdded();
      this.refreshCartCount();
      return;
    }

    // Default / cart mode / no drawer available.
    window.location = this.cartUrl();
  };

  LourelProduct.prototype.flashAdded = function () {
    if (!this.submitText) return;
    var original = this.submitText.textContent;
    this.submitText.textContent = "Added ✓";
    var self = this;
    setTimeout(function () {
      self.submitText.textContent = original;
    }, 1800);
  };

  LourelProduct.prototype.refreshCartCount = function () {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var nodes = document.querySelectorAll(
          ".cart-count-bubble span, [data-cart-count], .cart-count"
        );
        nodes.forEach(function (n) {
          n.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  };

  LourelProduct.prototype.setLoading = function (on) {
    if (this.submitBtn) {
      this.submitBtn.classList.toggle("is-loading", on);
      this.submitBtn.setAttribute("aria-busy", on ? "true" : "false");
    }
    if (this.spinner) this.spinner.classList.toggle("hidden", !on);
  };

  LourelProduct.prototype.showError = function (msg) {
    if (!this.errorBox) return;
    if (!msg) {
      this.errorBox.setAttribute("hidden", "");
      if (this.errorText) this.errorText.textContent = "";
      return;
    }
    this.errorBox.removeAttribute("hidden");
    if (this.errorText) this.errorText.textContent = msg;
  };

  /* ------------------------------- Boot --------------------------------- */
  function init(scope) {
    (scope || document)
      .querySelectorAll('[data-section-type="lourel-product"]')
      .forEach(function (el) {
        if (el.__lourelInit) return;
        el.__lourelInit = true;
        new LourelProduct(el);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  // Re-init when sections are loaded/reloaded in the theme editor.
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
})();
